import { del, get, keys, set } from 'idb-keyval'
import { PHOTO_EDGE, PHOTO_QUALITY, PHOTO_TYPE } from '@mads/shared'

/**
 * Photographs taken with no connection (issue #64, step 4).
 *
 * **The bytes are shrunk before they are stored, not after.** The issue framed
 * this step as holding originals in IndexedDB — tens of megabytes on a phone —
 * and that is exactly why it was left until last. It is also avoidable: what
 * the server keeps is a 600px JPEG and nothing else (issue #56), so a device
 * that produces the same thing before writing it stores about seventy
 * kilobytes instead of five megabytes. A walk round a building with thirty
 * photographs is then single-digit megabytes rather than a quota error nobody
 * can act on.
 *
 * What it costs is one extra JPEG generation, because the server re-encodes
 * what it is sent. At 600px and quality 82 that is not something anybody can
 * see, and it buys the feature being usable on the device it is for.
 *
 * What it *gains* is worth saying plainly: **a phone's GPS coordinates never
 * reach the disk.** Today they are stripped server-side on upload, which is
 * fine when the upload is immediate; a photograph queued for a day would
 * otherwise sit in IndexedDB with the building it was taken in written on it.
 * Drawing to a canvas reads pixels and writes pixels, so there is no metadata
 * to carry over — the same reason GD strips it server-side.
 *
 * **The blobs live under their own keys, one per changeset, and never inside
 * the queue.** The queue is a single JSON array written whole on every
 * enqueue; a photograph in it would be rewritten on every subsequent count.
 */

const KEY_PREFIX = 'mads.queue.photo'

function keyFor(userId: number, changesetId: string): string {
  return `${KEY_PREFIX}.${userId}.${changesetId}`
}

/**
 * Re-encodes a chosen file to what the server would keep.
 *
 * Two things this does that the server's GD path does not, and one it copies:
 *
 *   - **EXIF orientation is honoured** (`imageOrientation: 'from-image'`).
 *     A phone writes the sensor's pixels and a rotation flag beside them; GD
 *     ignores the flag, so a photograph taken in portrait is stored sideways.
 *     That is a pre-existing gap on the online path and issue #56's to close —
 *     what matters here is that a photograph taken offline is not made *worse*
 *     by being re-encoded on the device.
 *   - **A white canvas underneath**, copied from `ImageProcessor::fit()`: PNG
 *     and WebP may be transparent and JPEG has no alpha, so left alone the gaps
 *     come out black and a cut-out catalogue shot becomes a silhouette.
 *   - **Scaling down only.** Enlarging invents detail; a small photograph is a
 *     small photograph.
 */
export async function shrinkForQueue(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })

  try {
    const scale = Math.min(1, PHOTO_EDGE / Math.max(bitmap.width, bitmap.height))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')

    if (context === null) {
      throw new Error('This browser cannot prepare a photograph for sending later.')
    }

    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, width, height)
    context.drawImage(bitmap, 0, 0, width, height)

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob === null
            ? reject(new Error('That photograph could not be prepared for sending later.'))
            : resolve(blob),
        PHOTO_TYPE,
        PHOTO_QUALITY,
      )
    })
  } finally {
    bitmap.close()
  }
}

export async function putQueuedPhoto(
  userId: number,
  changesetId: string,
  photo: Blob,
): Promise<void> {
  await set(keyFor(userId, changesetId), photo)
}

export async function readQueuedPhoto(
  userId: number,
  changesetId: string,
): Promise<Blob | undefined> {
  return get<Blob>(keyFor(userId, changesetId))
}

export async function dropQueuedPhoto(userId: number, changesetId: string): Promise<void> {
  await del(keyFor(userId, changesetId))
}

/**
 * Drops every stored photograph whose changeset is no longer queued.
 *
 * Called wherever the queue is rewritten, because that is the only moment a
 * blob can become an orphan — a changeset that applied, or one the walker
 * discarded. An orphan is invisible and permanent otherwise: nothing else ever
 * looks at these keys, so nothing else would ever find it.
 */
export async function pruneQueuedPhotos(
  userId: number,
  liveChangesetIds: ReadonlySet<string>,
): Promise<void> {
  const prefix = `${KEY_PREFIX}.${userId}.`
  const stored = await keys()

  await Promise.all(
    stored
      .filter((key): key is string => typeof key === 'string' && key.startsWith(prefix))
      .filter((key) => !liveChangesetIds.has(key.slice(prefix.length)))
      .map((key) => del(key)),
  )
}

/**
 * A reference the record's `image` carries while its photograph is only on this
 * device. `RecordPhoto` reads it from here rather than from the API, which has
 * never heard of it.
 */
export function localPhotoUrl(changesetId: string): string {
  return `${LOCAL_SCHEME}${changesetId}`
}

const LOCAL_SCHEME = 'queued:'

export function queuedChangesetId(url: string): string | null {
  return url.startsWith(LOCAL_SCHEME) ? url.slice(LOCAL_SCHEME.length) : null
}
